import React, { createContext, useContext, useState } from 'react';

/**
 * Lets the Pour Dashboard page publish its section tabs (Dashboard / Location /
 * Strength / …) so the mobile bottom navigation can render them in place of the
 * global nav. The page owns the active index; the bottom bar drives it via
 * `onSelect`. When no page has registered, `nav` is null and the bottom bar
 * shows the default global destinations.
 */
export interface PourNavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
}

export interface PourTitle {
  project?: string;
  pour?: string;
}

export interface PourNavState {
  items: PourNavItem[];
  active: number;
  onSelect: (index: number) => void;
}

interface PourNavContextValue {
  nav: PourNavState | null;
  setNav: (nav: PourNavState | null) => void;
  /** Project / pour names shown in the app header while the dashboard is open. */
  title: PourTitle | null;
  setTitle: (title: PourTitle | null) => void;
}

const PourNavContext = createContext<PourNavContextValue>({
  nav: null,
  setNav: () => {},
  title: null,
  setTitle: () => {},
});

export const PourNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [nav, setNav] = useState<PourNavState | null>(null);
  const [title, setTitle] = useState<PourTitle | null>(null);
  return (
    <PourNavContext.Provider value={{ nav, setNav, title, setTitle }}>
      {children}
    </PourNavContext.Provider>
  );
};

export const usePourNav = (): PourNavContextValue => useContext(PourNavContext);
